import React, { useState, useEffect, useCallback } from 'react'
import { useParams, navigate } from '@reach/router'
import { useDispatch, useSelector } from 'react-redux'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import HeaderTitle from '_components/header-title'
import { Button as ReferaButton, Loader } from '@refera/ui-web'
import Button from '_components/button'
import { Grid, TextField, InputLabel } from '@mui/material'
import { BasicInput, Select } from '_/components/inputs'
import SwitchComponent from '_/components/switch'
import * as Input from '_/components/inputs/Input'
import { useDialog } from '_/hooks/use-dialog'

import useRolePermission from '_/hooks/use-role-permission'
import { useToast } from '_/hooks/use-toast'
import {
  getAssistantById,
  getAiModels,
  patchAssistant,
  postAssistant,
} from '_/modules/ai-integration/actions'
import { aiModelsListSelector } from '_modules/ai-integration/selectors'
import { AI_SERVICES, ASSISTANT_DEFAULT_VALUES, REASONING_EFFORT_OPTIONS } from '../utils/constants'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import AiTestModal from './AiTestModal'

import useStyles from './styles'

export const AiAssistantForm = ({ isEditing = true }) => {
  const styles = useStyles()
  const { assistantId } = useParams()
  const dispatch = useDispatch()

  const { checkUserPermission } = useRolePermission()
  const { showDialog, closeDialog } = useDialog()
  const { showToast } = useToast()
  const aiModels = useSelector(aiModelsListSelector)

  const [assistant, setAssistant] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [realTokenValue, setRealTokenValue] = useState('')
  const [typingTimeout, setTypingTimeout] = useState(null)
  const [isTestModalOpen, setIsTestModalOpen] = useState(false)

  const methods = useForm({
    mode: 'all',
    defaultValues: ASSISTANT_DEFAULT_VALUES,
  })

  const {
    handleSubmit,
    reset,
    getValues,
    control,
    formState: { dirtyFields, isValid },
  } = methods

  const handleBackNavigation = useCallback(() => {
    navigate(-1)
  }, [])

  const handleTokenChange = useCallback(
    onChange => event => {
      const newValue = event.target.value
      setRealTokenValue(newValue)
      onChange(newValue)

      if (typingTimeout) {
        clearTimeout(typingTimeout)
        setTypingTimeout(null)
      }

      const newTimeout = window.setTimeout(() => {
        if (newValue) {
          const maskedValue = '*'.repeat(newValue.length)
          onChange(maskedValue)
        }
      }, 1500)

      setTypingTimeout(newTimeout)
    },
    [typingTimeout]
  )

  const handleTokenFocus = useCallback(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout)
      setTypingTimeout(null)
    }
  }, [typingTimeout])

  const handleTokenBlur = useCallback(
    onChange => () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout)
        setTypingTimeout(null)
      }
      if (realTokenValue) {
        const maskedValue = '*'.repeat(realTokenValue.length)
        onChange(maskedValue)
      }
    },
    [realTokenValue, typingTimeout]
  )

  const resetFormWithData = useCallback(
    data => {
      const tokenValue = data?.tokenApi || ''
      setRealTokenValue('')
      reset({
        name: data?.name || '',
        slug: data?.slug || '',
        description: data?.description || '',
        initialPrompt: data?.initialPrompt || '',
        tokenApi: tokenValue,
        temperature: data?.temperature || 0,
        maxTokens: data?.maxTokens || 0,
        frequencyPenalty: data?.frequencyPenalty || 0,
        presencePenalty: data?.presencePenalty || 0,
        reasoningEffort: data?.reasoningEffort || '',
        service: data?.service || '',
        model: data?.model || '',
        isActive: data?.isActive ?? false,
      })
    },
    [reset]
  )

  useEffect(() => {
    if (assistantId) {
      dispatch(getAssistantById(assistantId)).then(data => {
        setAssistant(data)
        resetFormWithData(data)
      })
    }
  }, [assistantId])

  useEffect(() => {
    if (assistant) {
      const { setValue } = methods
      setValue('description', assistant?.description || '')
      setValue('initialPrompt', assistant?.initialPrompt || '')
      setValue('isActive', assistant?.isActive ?? false)
      const tokenValue = assistant?.tokenApi || ''
      setValue('tokenApi', tokenValue)
      setRealTokenValue('')
    }
  }, [assistant])

  useEffect(() => {
    if (!aiModels || aiModels.length === 0) {
      dispatch(getAiModels())
    }
  }, [aiModels])

  const handleCancel = () => {
    if (Object.keys(dirtyFields).length > 0) {
      showDialog({
        type: 'warning',
        subject: 'Você tem certeza que deseja cancelar estas alterações?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          closeDialog()
          navigate(-1)
          reset()
        },
        onCancel: closeDialog,
      })
    } else {
      navigate(-1)
    }
  }

  function toNumber(value) {
    return Number(String(value).replace(',', '.') || 0)
  }

  const onSubmit = useCallback(
    async data => {
      setIsLoading(true)

      const transformedData = {
        ...data,
        temperature: toNumber(data.temperature),
        frequencyPenalty: toNumber(data.frequencyPenalty),
        presencePenalty: toNumber(data.presencePenalty),
        reasoningEffort: data.reasoningEffort || null,
      }

      if (realTokenValue) {
        transformedData.tokenApi = realTokenValue
      } else if (assistantId && data.tokenApi && data.tokenApi.includes('*')) {
        delete transformedData.tokenApi
      }

      const action = assistantId
        ? patchAssistant(assistantId, transformedData)
        : postAssistant(transformedData)

      dispatch(action)
        .then(() => {
          showToast({
            type: 'success',
          })
          navigate('/assistentes-ia')
        })
        .catch(() => {
          showToast({
            type: 'error',
          })
        })
        .finally(() => setIsLoading(false))
    },
    [isValid, assistantId, realTokenValue]
  )

  return (
    <>
      <Loader hasBackdrop open={isLoading} />
      <FormProvider {...methods}>
        <form onSubmit={e => e.preventDefault()}>
          <HeaderTitle title="IA - Assistente" backButtonAction={handleBackNavigation}>
            {isEditing && (
              <Grid className={styles.headerButtons}>
                {assistantId && (
                  <ReferaButton
                    className={styles.button}
                    variant="secondary"
                    onClick={() => setIsTestModalOpen(true)}
                  >
                    Testar
                  </ReferaButton>
                )}
                <ReferaButton
                  className={styles.button}
                  color="red"
                  variant="secondary"
                  onClick={handleCancel}
                >
                  Cancelar
                </ReferaButton>
                <Button
                  className={styles.button}
                  color="primary"
                  variant="contained"
                  disabled={isLoading || !isValid}
                  onClick={() =>
                    checkUserPermission({
                      permission: PERMISSIONS.AI_TRAINING,
                      permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
                      functionAccess: () => handleSubmit(onSubmit)(),
                    })
                  }
                >
                  Salvar
                </Button>
              </Grid>
            )}
          </HeaderTitle>

          <Grid className={styles.container}>
            <div>
              <BasicInput
                name="name"
                label="Nome do assistente"
                disabled={!isEditing}
                defaultValue={assistant?.name || ''}
                rules={{
                  maxLength: {
                    value: 100,
                    message: `Esse campo aceita no máximo 100 caracteres`,
                  },
                }}
              />
            </div>

            <div className={styles.slug}>
              <BasicInput
                name="slug"
                label="Slug"
                disabled={!isEditing}
                defaultValue={assistant?.slug || ''}
                rules={{
                  maxLength: {
                    value: 100,
                    message: `Esse campo aceita no máximo 100 caracteres`,
                  },
                }}
              />
            </div>

            <div className={styles.description}>
              <InputLabel variant="standard" className={styles.label}>
                Descrição
              </InputLabel>
              <Controller
                name="description"
                control={control}
                render={({ value, name, onChange }) => {
                  return (
                    <TextField
                      name={name}
                      value={value}
                      minRows={3}
                      maxRows={10}
                      fullWidth
                      multiline
                      variant="outlined"
                      InputProps={{ style: { fontSize: '1.4rem', paddingLeft: '8px' } }}
                      onChange={onChange}
                      disabled={!isEditing}
                    />
                  )
                }}
              />
            </div>

            <div className={styles.initialPrompt}>
              <InputLabel variant="standard" className={styles.label}>
                Prompt inicial
              </InputLabel>
              <Controller
                name="initialPrompt"
                control={control}
                render={({ value, name, onChange }) => {
                  return (
                    <TextField
                      name={name}
                      value={value}
                      minRows={3}
                      maxRows={10}
                      fullWidth
                      multiline
                      variant="outlined"
                      InputProps={{ style: { fontSize: '1.4rem', paddingLeft: '8px' } }}
                      onChange={onChange}
                      disabled={!isEditing}
                    />
                  )
                }}
              />
            </div>
            <div className={styles.tokenApi}>
              <InputLabel variant="standard" className={styles.label}>
                Token API
              </InputLabel>
              <Controller
                name="tokenApi"
                control={control}
                render={({ value, name, onChange }) => {
                  return (
                    <TextField
                      name={name}
                      value={value}
                      fullWidth
                      variant="outlined"
                      InputProps={{ style: { fontSize: '1.4rem', paddingLeft: '8px' } }}
                      onChange={handleTokenChange(onChange)}
                      onFocus={handleTokenFocus(onChange)}
                      onBlur={handleTokenBlur(onChange)}
                      disabled={!isEditing}
                      autoComplete="new-password"
                    />
                  )
                }}
              />
            </div>

            <div className={styles.temperature}>
              <Input.Root name="temperature" defaultValue={assistant?.temperature || 0}>
                <Input.Label name="temperature">Temperatura</Input.Label>
                <Input.ControllerNumber name="temperature" decimalScale={1} />
              </Input.Root>
            </div>

            <div className={styles.maxTokens}>
              <Input.Root name="maxTokens" defaultValue={assistant?.maxTokens || 0}>
                <Input.Label name="maxTokens">Máximo de tokens</Input.Label>
                <Input.ControllerNumber name="maxTokens" decimalScale={0} />
              </Input.Root>
            </div>
            <div className={styles.frequencyPenalty}>
              <Input.Root name="frequencyPenalty" defaultValue={assistant?.frequencyPenalty || 0}>
                <Input.Label name="frequencyPenalty">Penalidade de frequência</Input.Label>
                <Input.ControllerNumber name="frequencyPenalty" decimalScale={1} />
              </Input.Root>
            </div>

            <div className={styles.presencePenalty}>
              <Input.Root name="presencePenalty" defaultValue={assistant?.presencePenalty || 0}>
                <Input.Label name="presencePenalty">Penalidade de presença</Input.Label>
                <Input.ControllerNumber name="presencePenalty" decimalScale={1} />
              </Input.Root>
            </div>

            <div className={styles.service}>
              <Select
                label="Serviço"
                name="service"
                options={AI_SERVICES}
                getLabel={item => item.desc}
                getValue={item => item.name}
                defaultValue={assistant?.service || ''}
                disabled={!isEditing}
              />
            </div>

            <div className={styles.model}>
              <Select
                label="Modelo de IA"
                name="model"
                options={aiModels}
                getLabel={item => item.name}
                getValue={item => item.name}
                defaultValue={assistant?.model || ''}
                disabled={!isEditing}
              />
            </div>

            <div className={styles.reasoningEffort}>
              <Select
                label="Esforço de raciocínio"
                name="reasoningEffort"
                options={REASONING_EFFORT_OPTIONS}
                getLabel={item => item.label}
                getValue={item => item.value}
                getKey={item => item.value}
                defaultValue={assistant?.reasoningEffort || ''}
                disabled={!isEditing}
                emptyValue="Nenhum (null)"
              />
            </div>

            <div className={styles.isActive}>
              <InputLabel variant="standard">Ativo</InputLabel>
              <Controller
                name="isActive"
                control={control}
                render={({ value, name, onChange }) => {
                  return (
                    <SwitchComponent
                      name={name}
                      checked={value}
                      onChange={e => onChange(e.target.checked)}
                      disabled={!isEditing}
                    />
                  )
                }}
              />
            </div>
          </Grid>
        </form>
      </FormProvider>
      {isTestModalOpen && (
        <AiTestModal
          open={isTestModalOpen}
          onClose={didPatch => {
            setIsTestModalOpen(false)
            if (didPatch && assistantId) {
              dispatch(getAssistantById(assistantId)).then(data => {
                setAssistant(data)
              })
            }
          }}
          assistantId={assistantId}
          formValues={{ ...getValues(), slug: assistant?.slug }}
        />
      )}
    </>
  )
}
